import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { ROUTES } from "../../router/constants";
import dayjs from "../../lib/dayjs";
import {
  listAuditLogs,
  type AuditAction,
  type AuditOrigin,
  type AuditLogItem,
} from "../../services/auditLogs.service";
import { getUserById } from "../../services/usersList.service";

const ACTIONS: AuditAction[] = [
  "CREATE",
  "UPDATE",
  "DELETE",
  "LOGIN",
  "LOGOUT",
  "PAYMENT",
  "CANCEL",
  "STATUS_CHANGE",
];

const ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: "Criação",
  UPDATE: "Atualização",
  DELETE: "Exclusão",
  LOGIN: "Login",
  LOGOUT: "Logout",
  PAYMENT: "Pagamento",
  CANCEL: "Cancelamento",
  STATUS_CHANGE: "Alteração de status",
};

const ORIGINS: AuditOrigin[] = ["WEB", "MOBILE", "API", "WEBHOOK", "CRON", "SYSTEM"];

const ORIGIN_LABELS: Record<AuditOrigin, string> = {
  WEB: "Web",
  MOBILE: "Mobile",
  API: "API",
  WEBHOOK: "Webhook",
  CRON: "Tarefa agendada (CRON)",
  SYSTEM: "Sistema interno",
};

export function AuditLogs() {
  const navigate = useNavigate();
  const { user: loggedUser } = useSelector((state: RootState) => state.userInfo);

  const roleUpper = String(loggedUser?.role || "").toUpperCase();
  const allowedRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER"] as const;
  const isAllowed = allowedRoles.includes(roleUpper as (typeof allowedRoles)[number]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  // cache local de nomes por userId para exibir nos logs
  const [userNamesById, setUserNamesById] = useState<Record<string, string>>({});
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // filtros
  const [q, setQ] = useState("");
  const [action, setAction] = useState<"all" | AuditAction>("all");
  const [origin, setOrigin] = useState<"all" | AuditOrigin>("all");
  const [success, setSuccess] = useState<"all" | "true" | "false">("all");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  // guard
  useEffect(() => {
    if (!isAllowed) {
      const isUserRole = roleUpper === "USER";
      navigate(isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAllowed, roleUpper, navigate]);

  const queryParams = useMemo(() => {
    return {
      page,
      perPage,
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(action !== "all" ? { action } : {}),
      ...(origin !== "all" ? { origin } : {}),
      ...(success !== "all" ? { success: success === "true" } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    };
  }, [page, perPage, q, action, origin, success, from, to]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await listAuditLogs(queryParams as any);
      setItems(res.items || []);
      setPage(res.pagination.page);
      setPerPage(res.pagination.perPage);
      setTotalPages(res.pagination.totalPages);
      setTotalItems(res.pagination.totalItems);
    } catch (e: any) {
      console.error(e);
      setError("Não foi possível carregar os logs. Tente novamente.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed, queryParams]);

  // Quando carregamos novos itens, buscamos (em segundo plano) os nomes dos usuários afetados
  useEffect(() => {
    if (!items.length) return;

    const ids = Array.from(
      new Set(
        items
          .map((it) => it.userId)
          .filter((id): id is string => !!id && !userNamesById[id]),
      ),
    );

    if (!ids.length) return;

    let cancelled = false;

    async function loadUserNames() {
      const entries: [string, string][] = [];

      for (const id of ids) {
        try {
          const user = await getUserById(id);
          if (user?.name) {
            entries.push([id, user.name]);
          }
        } catch (err) {
          // não quebra a tela se algum usuário não carregar
          console.error("Erro ao carregar usuário do log", err);
        }
      }

      if (!cancelled && entries.length) {
        setUserNamesById((prev) => ({
          ...prev,
          ...Object.fromEntries(entries),
        }));
      }
    }

    loadUserNames();

    return () => {
      cancelled = true;
    };
  }, [items, userNamesById]);

  const handleApplyFilters = () => {
    // sempre volta para primeira página quando mexer nos filtros
    setPage(1);
    load();
  };

  const handleClearFilters = () => {
    setQ("");
    setAction("all");
    setOrigin("all");
    setSuccess("all");
    setFrom("");
    setTo("");
    setPage(1);
  };

  return (
    <Box
      width="100%"
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", mt: 6 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Logs de auditoria
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Consulte eventos de criação/alteração/exclusão e ações do sistema (login, pagamentos, etc.).
        </Typography>

        <Card sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Busca (q)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="action-filter-label">Ação</InputLabel>
                <Select
                  labelId="action-filter-label"
                  label="Ação"
                  value={action}
                  onChange={(e) => setAction(e.target.value as any)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {ACTIONS.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="origin-filter-label">Origem</InputLabel>
                <Select
                  labelId="origin-filter-label"
                  label="Origem"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value as any)}
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {ORIGINS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="success-filter-label">Sucesso</InputLabel>
                <Select
                  labelId="success-filter-label"
                  label="Sucesso"
                  value={success}
                  onChange={(e) => setSuccess(e.target.value as any)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="true">Sucesso</MenuItem>
                  <MenuItem value="false">Falha</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="De"
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Até"
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="per-page-label">Por página</InputLabel>
                <Select
                  labelId="per-page-label"
                  label="Por página"
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClearFilters} disabled={loading}>
                Limpar
              </Button>
              <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
                Aplicar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" mt={2}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Total: {totalItems}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Ação</TableCell>
                    <TableCell>Entidade</TableCell>
                    <TableCell>ID da entidade</TableCell>
                    <TableCell>Usuário (nome)</TableCell>
                    <TableCell>Perfil</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell>Descrição</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it) => {
                    const dateLabel = it.createdAt
                      ? dayjs(it.createdAt).format("DD/MM/YYYY HH:mm")
                      : "-";

                    return (
                      <TableRow key={it.id} hover>
                        <TableCell>{dateLabel}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={ACTION_LABELS[it.action] || it.action}
                            title={it.action}
                          />
                        </TableCell>
                        <TableCell>{it.entity ?? "-"}</TableCell>
                        <TableCell>{it.entityId ?? "-"}</TableCell>
                        <TableCell title={it.userId ?? undefined}>
                          {it.userName || (it.userId ? userNamesById[it.userId] : null) || it.userId || "-"}
                        </TableCell>
                        <TableCell>{it.userRole ?? "-"}</TableCell>
                        <TableCell>
                          {it.origin ? ORIGIN_LABELS[it.origin] || it.origin : "-"}
                        </TableCell>
                        <TableCell>{it.ipAddress ?? "-"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={it.success ? "Sucesso" : "Falha"}
                            color={it.success ? "success" : "error"}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          {it.description ?? it.errorMessage ?? "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Nenhum log encontrado com os filtros atuais.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={loading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                Página {page} de {totalPages}
              </Typography>
              <Button
                variant="outlined"
                disabled={loading || page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
